import { Box, Chip, CircularProgress, Tooltip } from "@mui/material";
import { TIER_CONFIG, LEVEL_CONFIG } from "network/apis/badges";
import { BadgeTier } from "network/apis/users";

interface BadgeTierChipProps {
  tier?: BadgeTier;
  size?: "small" | "medium";
  quarterPoints?: number;
  pointsToNextTier?: number | null;
  nextTier?: string | null;
}

const BadgeTierChip = ({
  tier,
  size = "small",
  quarterPoints,
  pointsToNextTier,
  nextTier
}: BadgeTierChipProps) => {
  if (!tier) return null;
  const config = TIER_CONFIG[tier];
  if (!config) return null;

  // Calculate progress percentage toward next tier
  const progress = (() => {
    if (!pointsToNextTier || !nextTier) return 100; // max tier
    const total = (quarterPoints || 0) + pointsToNextTier;
    return total > 0 ? ((quarterPoints || 0) / total) * 100 : 0;
  })();

  const showProgress = quarterPoints !== undefined;
  const isMaxTier = !nextTier;

  const tooltipText = isMaxTier
    ? `${config.label} — Max tier!`
    : `${quarterPoints || 0} pts · ${pointsToNextTier} to ${
        TIER_CONFIG[nextTier!]?.label || nextTier
      }`;

  const chip = (
    <Chip
      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {showProgress && (
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                width: 14,
                height: 14
              }}
            >
              <CircularProgress
                variant="determinate"
                value={100}
                size={14}
                thickness={5}
                sx={{ color: `${config.color}25`, position: "absolute" }}
              />
              <CircularProgress
                variant="determinate"
                value={progress}
                size={14}
                thickness={5}
                sx={{ color: config.color }}
              />
            </Box>
          )}
          <span>
            {config.icon} {config.label}
          </span>
        </Box>
      }
      size={size}
      sx={{
        bgcolor: config.bgColor,
        color: config.color,
        fontWeight: 600,
        fontSize: size === "small" ? "0.65rem" : "0.75rem",
        height: size === "small" ? "20px" : "24px",
        border: `1px solid ${config.color}20`,
        ml: 0.5,
        "& .MuiChip-label": { display: "flex", alignItems: "center", px: 0.75 }
      }}
    />
  );

  if (showProgress) {
    return (
      <Tooltip title={tooltipText} arrow placement="bottom">
        {chip}
      </Tooltip>
    );
  }

  return chip;
};

interface LifetimeLevelChipProps {
  level?: number | null;
  size?: "small" | "medium";
  plusCount?: number;
  pointsToNext?: number | null;
  nextLevel?: number | null;
}

export const LifetimeLevelChip = ({
  level,
  size = "small",
  plusCount,
  pointsToNext,
  nextLevel
}: LifetimeLevelChipProps) => {
  if (!level) return null;
  const config = LEVEL_CONFIG[level];
  if (!config) return null;

  // Calculate progress percentage toward next level
  const progress = (() => {
    if (!pointsToNext || !nextLevel) return 100; // max level
    const total = (plusCount || 0) + pointsToNext;
    return total > 0 ? ((plusCount || 0) / total) * 100 : 0;
  })();

  const showProgress = plusCount !== undefined;
  const isMaxLevel = !nextLevel;

  const tooltipText = isMaxLevel
    ? `${config.label} — Max level!`
    : `${plusCount || 0} pts · ${pointsToNext} to Level ${nextLevel}`;

  const chip = (
    <Chip
      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {showProgress && (
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                width: 14,
                height: 14
              }}
            >
              <CircularProgress
                variant="determinate"
                value={100}
                size={14}
                thickness={5}
                sx={{ color: `${config.color}25`, position: "absolute" }}
              />
              <CircularProgress
                variant="determinate"
                value={progress}
                size={14}
                thickness={5}
                sx={{ color: config.color }}
              />
            </Box>
          )}
          <span>
            {config.icon} Level {level}
          </span>
        </Box>
      }
      size={size}
      sx={{
        bgcolor: "#F3F4F6",
        color: config.color,
        fontWeight: 600,
        fontSize: size === "small" ? "0.65rem" : "0.75rem",
        height: size === "small" ? "20px" : "24px",
        border: `1px solid ${config.color}20`,
        ml: 0.5,
        "& .MuiChip-label": { display: "flex", alignItems: "center", px: 0.75 }
      }}
    />
  );

  if (showProgress) {
    return (
      <Tooltip title={tooltipText} arrow placement="bottom">
        {chip}
      </Tooltip>
    );
  }

  return chip;
};

export default BadgeTierChip;
